/* eslint-disable react/no-unescaped-entities */
import React, { useRef, useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Image, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownLong, faBars } from '@fortawesome/free-solid-svg-icons'
import { mobilePhone, wavenet_logo } from '../asset'
import CIIcon from './CIIcon'

function PizzaPreview({ srcs }) {
  const validImages = Array.isArray(srcs) ? srcs.filter((src) => src.file) : []
  const pizzaRef = useRef(null)
  const [rotation, setRotation] = useState(0)
  const [hasRotated, setHasRotated] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const isDragging = useRef(false)
  const startAngle = useRef(0)
  const velocity = useRef(0)

  useEffect(() => {}, [validImages])

  const getCurrentSection = () => {
    const baseAngle = 30
    const normalizedRotation = (((rotation - baseAngle) % 360) + 360) % 360

    if (
      (normalizedRotation >= 272.47 && normalizedRotation <= 360) ||
      (normalizedRotation >= 0 && normalizedRotation < 32.72)
    ) {
      return 'A'
    }
    if (normalizedRotation >= 32.72 && normalizedRotation < 152.72) {
      return 'C'
    }
    return 'B'
  }

  const getPopupImage = () => {
    if (validImages.length < 4) return null

    const section = getCurrentSection()

    if (section === 'A') return validImages[1]?.file ?? null
    if (section === 'B') return validImages[2]?.file ?? null
    if (section === 'C') return validImages[3]?.file ?? null

    return null
  }

  const getAngle = (e) => {
    if (!pizzaRef.current) return 0
    const rect = pizzaRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const x = e.clientX - centerX
    const y = e.clientY - centerY
    return Math.atan2(y, x) * (180 / Math.PI)
  }

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      const newAngle = getAngle(e)
      const angleChange = newAngle - startAngle.current
      velocity.current = angleChange
      setRotation((prev) => prev + angleChange)
      startAngle.current = newAngle

      if (!hasRotated) {
        setHasRotated(true)
      }
    }
  }

  const handleMouseUp = () => {
    isDragging.current = false
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)

    const popupImage = getPopupImage()

    if (validImages.length >= 4 && popupImage) {
      setTimeout(() => {
        setShowPopup(true)
      }, 1600)
    }

    let inertia = velocity.current * 10
    const friction = 0.9

    const applyFriction = () => {
      if (Math.abs(inertia) > 0.1) {
        inertia *= friction
        setRotation((prev) => prev + inertia)
        requestAnimationFrame(applyFriction)
      } else {
        velocity.current = 0
      }
    }

    applyFriction()
  }

  // 要寫一個旋轉大於30度才能跑出結果的功能

  const handleMouseDown = (e) => {
    isDragging.current = true
    startAngle.current = getAngle(e)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleClosePopup = () => {
    setShowPopup(false)
  }

  const pizzaDisplay = useMemo(() => {
    if (validImages.length === 0) return 'none'
    return showPopup ? 'none' : 'flex'
  }, [validImages, showPopup])

  return (
    <div
      className="scroller-hide"
      style={{ maxHeight: '57.5vh', overflow: 'auto' }}
    >
      {/* 頂部內容 */}
      <div className="w-100 pb-1">
        <Image
          className="position-absolute"
          src={mobilePhone}
          style={{ width: '19.5rem', right: '16%', top: '6%' }}
        />
      </div>
      <div style={{ position: 'relative' }}>
        <div
          className="border-bottom mb-3 d-flex"
          style={{ width: '240px', height: '55px', zIndex: '0' }}
        >
          <div className="w-80 m-auto ms-auto">
            <Image src={wavenet_logo} className="w-75 ps-3" />
          </div>
          <div className="w-20 m-auto ms-auto d-flex">
            <FontAwesomeIcon icon={faBars} className="text-end" />
          </div>
        </div>
        <div className="text-title" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-ban1" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line2" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <br />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
      </div>

      <div
        id="PizzaRotate"
        className="d-flex w-100 position-relative px-3 py-2"
        style={{
          height: showPopup ? '0px' : '230px',
          width: showPopup ? '0px' : '300px',
          display: pizzaDisplay,
        }}
      >
        <FontAwesomeIcon
          icon={faDownLong}
          className="spin-pointer"
          style={{ display: showPopup ? 'none' : 'flex' }}
        />

        <div
          ref={pizzaRef}
          className="position-relative w-100 h-100 rounded-full mx-auto overflow-hidden bg-gray-300"
          style={{
            borderRadius: '50%',
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.1s linear',
            userSelect: 'none',
          }}
          onMouseDown={handleMouseDown}
          role="presentation"
        >
          {!showPopup && validImages.length > 0 && (
            <img
              className="position-absolute w-100 h-100 object-cover"
              src={
                validImages[0].file instanceof File ||
                validImages[0].file instanceof Blob
                  ? URL.createObjectURL(validImages[0].file)
                  : validImages[0].file
              }
              alt={validImages[0].label}
              draggable="false"
            />
          )}
        </div>
      </div>

      {showPopup && getPopupImage() && (
        <div className="popup-container">
          <img
            className="popup-image"
            src={
              getPopupImage() instanceof File || getPopupImage() instanceof Blob
                ? URL.createObjectURL(getPopupImage())
                : getPopupImage()
            }
            alt="Popup"
          />
          <Button className="close-button" onClick={handleClosePopup}>
            ✕
          </Button>
          <CIIcon />
        </div>
      )}

      {/* 底部內容 */}
      <div style={{ position: 'relative', paddingTop: '1rem' }}>
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <br />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
        <div className="text-line" />
      </div>
    </div>
  )
}

PizzaPreview.propTypes = {
  srcs: PropTypes.arrayOf(
    PropTypes.shape({
      file: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(File),
        PropTypes.instanceOf(Blob),
      ]),
      label: PropTypes.string,
    })
  ),
}

PizzaPreview.defaultProps = {
  srcs: [],
}

export default PizzaPreview
